import React, { useState, useEffect } from "react";
import LeftNav from './common/LeftNav';
import TopNav from './common/TopNav';
import { useNavigate } from "react-router-dom";
import { FaArrowLeft, FaClock, FaReceipt, FaBell } from "react-icons/fa";
import Toaster from "../../components/Toaster";

// Import services
import authService from "../../services/authService";
import { apiGet, apiPut } from "../../services/apiUtils";
import billingService from "../../services/billingService";

export default function AdminConfig() {
  const navigate = useNavigate();

  // State management
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tatDays, setTatDays] = useState('90');
  const [gstPercent, setGstPercent] = useState('18');
  const [creditPrice, setCreditPrice] = useState('1');

  // Toast state
  const [toast, setToast] = useState({
    show: false,
    message: '',
    type: 'success'
  });

  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
  };

  const hideToast = () => {
    setToast(prev => ({ ...prev, show: false }));
  };

  // Load configuration on component mount
  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      setLoading(true);
      // Use actual API calls matching mobile app exactly
      const [configRes, creditRes] = await Promise.all([
        apiGet('/api/admin/app-config').catch(() => null),
        billingService.getAdminCreditPrice().catch(() => null),
      ]);

      if (configRes?.success && configRes?.data) {
        setTatDays(configRes.data.tat_days || '90');
        setGstPercent(configRes.data.gst_rate || '18');
      } else if (configRes) {
        // Handle direct response format
        setTatDays(configRes.tat_days || '90');
        setGstPercent(configRes.gst_rate || '18');
      }
      if (creditRes?.success && creditRes?.value) {
        setCreditPrice(String(creditRes.value));
      }
    } catch (error) {
      console.error('Error loading config:', error);
      showToast('Failed to load configuration', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateCreditPrice = async () => {
    if (!creditPrice) return;
    try {
      setSaving(true);
      console.log('Updating credit price to:', creditPrice);
      const response = await billingService.updateAdminCreditPrice(creditPrice);
      if (response?.success) {
        showToast('Credit price updated successfully', 'success');
      } else {
        showToast(response?.message || 'Failed to update credit price', 'error');
      }
    } catch (error) {
      showToast('Failed to update credit price', 'error');
    } finally {
      setSaving(false);
    }
  };

  const saveConfig = async (key, value, label) => {
    try {
      setSaving(true);
      console.log(`Updating ${key} to:`, value);
      const response = await apiPut('/api/admin/app-config', { key, value });
      // apiPut returns data directly, so check for success
      if (response) {
        showToast(`${label} updated successfully`, 'success');
      } else {
        showToast('Failed to update configuration', 'error');
      }
    } catch (error) {
      showToast('Failed to update configuration', 'error');
    } finally {
      setSaving(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div>
        <LeftNav />
        <div className="main-content" style={{
          marginLeft: "250px",
          position: "absolute",
          top: "0px",
          width: "-webkit-fill-available"
        }}>
          <TopNav />
          <div className="p-3" style={{ marginTop: '70px' }}>
            {/* Header */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "16px 24px",
                backgroundColor: "#fff",
                borderRadius: "12px",
                border: "1px solid #E5E7EB",
                marginBottom: "24px"
              }}
            >
              <button
                onClick={() => navigate(-1)}
                style={{
                  background: "transparent",
                  border: "none",
                  cursor: "pointer",
                  padding: "8px",
                  marginRight: "16px"
                }}
              >
                <FaArrowLeft size={18} color="#000" />
              </button>
              <h1 style={{
                fontSize: "18px",
                fontWeight: "700",
                color: "#111827",
                margin: 0,
                flex: 1
              }}>
                Configuration
              </h1>
              <div style={{ width: "24px" }}></div>
            </div>

            {/* Loading spinner */}
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <div className="mt-3" style={{ color: '#888' }}>Loading...</div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <LeftNav />
      <div className="main-content" style={{
        marginLeft: "250px",
        position: "absolute",
        top: "0px",
        width: "-webkit-fill-available"
      }}>
        <TopNav />
        <div className="p-3" style={{ marginTop: '70px' }}>
          {/* Header */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "16px 24px",
              backgroundColor: "#fff",
              borderRadius: "12px",
              border: "1px solid #E5E7EB",
              marginBottom: "24px"
            }}
          >
            <button
              onClick={() => navigate(-1)}
              style={{
                background: "transparent",
                border: "none",
                cursor: "pointer",
                padding: "8px",
                marginRight: "16px"
              }}
            >
              <FaArrowLeft size={18} color="#000" />
            </button>
            <h1 style={{
              fontSize: "18px",
              fontWeight: "700",
              color: "#111827",
              margin: 0,
              flex: 1
            }}>
              Configuration
            </h1>
            <div style={{ width: "24px" }}></div>
          </div>

          {/* Content */}
          <div style={{ padding: "0 16px" }}>

            {/* TAT Section */}
            <div style={{
              backgroundColor: "#fff",
              borderRadius: "12px",
              padding: "20px",
              marginBottom: "16px",
              boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
              border: "1px solid #E5E7EB"
            }}>
              <div style={{
                display: "flex",
                alignItems: "center",
                marginBottom: "8px"
              }}>
                <FaClock size={22} color="#6366F1" />
                <h3 style={{
                  fontSize: "16px",
                  fontWeight: "700",
                  color: "#222",
                  marginLeft: "10px",
                  margin: 0
                }}>
                  TAT Time (Turn Around Time)
                </h3>
              </div>
              <p style={{
                fontSize: "13px",
                color: "#666",
                lineHeight: "18px",
                marginBottom: "16px"
              }}>
                Duration in days before builder can edit customer details for agent/agency registered leads.
              </p>
              <div style={{
                display: "flex",
                alignItems: "center",
                marginBottom: "16px"
              }}>
                <input
                  type="number"
                  value={tatDays}
                  onChange={(e) => setTatDays(e.target.value)}
                  placeholder="90"
                  style={{
                    flex: 1,
                    border: "1px solid #DDD",
                    borderRadius: "8px",
                    padding: "12px",
                    fontSize: "18px",
                    fontWeight: "600",
                    color: "#000",
                    backgroundColor: "#F9FAFB",
                    maxWidth: "120px"
                  }}
                />
                <span style={{
                  fontSize: "16px",
                  color: "#666",
                  fontWeight: "500",
                  marginLeft: "12px"
                }}>
                  days
                </span>
              </div>
              <button
                onClick={() => saveConfig('tat_days', tatDays, 'TAT Time')}
                disabled={saving}
                style={{
                  backgroundColor: "#6366F1",
                  borderRadius: "8px",
                  padding: "12px 24px",
                  border: "none",
                  color: "#fff",
                  fontSize: "15px",
                  fontWeight: "600",
                  cursor: saving ? "not-allowed" : "pointer",
                  opacity: saving ? 0.6 : 1
                }}
              >
                Save TAT
              </button>
            </div>

            {/* GST Section */}
            <div style={{
              backgroundColor: "#fff",
              borderRadius: "12px",
              padding: "20px",
              marginBottom: "16px",
              boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
              border: "1px solid #E5E7EB"
            }}>
              <div style={{
                display: "flex",
                alignItems: "center",
                marginBottom: "8px"
              }}>
                <FaReceipt size={22} color="#6366F1" />
                <h3 style={{
                  fontSize: "16px",
                  fontWeight: "700",
                  color: "#222",
                  marginLeft: "10px",
                  margin: 0
                }}>
                  GST Percentage
                </h3>
              </div>
              <p style={{
                fontSize: "13px",
                color: "#666",
                lineHeight: "18px",
                marginBottom: "16px"
              }}>
                GST percentage applied to all subscription plan payments. This will be added on top of the plan price.
              </p>
              <div style={{
                display: "flex",
                alignItems: "center",
                marginBottom: "16px"
              }}>
                <input
                  type="number"
                  step="0.1"
                  value={gstPercent}
                  onChange={(e) => setGstPercent(e.target.value)}
                  placeholder="18"
                  style={{
                    flex: 1,
                    border: "1px solid #DDD",
                    borderRadius: "8px",
                    padding: "12px",
                    fontSize: "18px",
                    fontWeight: "600",
                    color: "#000",
                    backgroundColor: "#F9FAFB",
                    maxWidth: "120px"
                  }}
                />
                <span style={{
                  fontSize: "16px",
                  color: "#666",
                  fontWeight: "500",
                  marginLeft: "12px"
                }}>
                  %
                </span>
              </div>
              <button
                onClick={() => saveConfig('gst_rate', gstPercent, 'GST Percentage')}
                disabled={saving}
                style={{
                  backgroundColor: "#6366F1",
                  borderRadius: "8px",
                  padding: "12px 24px",
                  border: "none",
                  color: "#fff",
                  fontSize: "15px",
                  fontWeight: "600",
                  cursor: saving ? "not-allowed" : "pointer",
                  opacity: saving ? 0.6 : 1
                }}
              >
                Save GST
              </button>
            </div>

            {/* Push Notification Credits Section */}
            <div style={{
              backgroundColor: "#fff",
              borderRadius: "12px",
              padding: "20px",
              marginBottom: "16px",
              boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
              border: "1px solid #E5E7EB"
            }}>
              <div style={{
                display: "flex",
                alignItems: "center",
                marginBottom: "8px"
              }}>
                <FaBell size={22} color="#6366F1" />
                <h3 style={{
                  fontSize: "16px",
                  fontWeight: "700",
                  color: "#222",
                  marginLeft: "10px",
                  margin: 0
                }}>
                  Push Notification Credits
                </h3>
              </div>
              <p style={{
                fontSize: "13px",
                color: "#666",
                lineHeight: "18px",
                marginBottom: "16px"
              }}>
                Base price per credit for push notifications. This price is used to calculate the cost of credit packages for builders.
              </p>
              <div style={{
                display: "flex",
                alignItems: "center",
                marginBottom: "16px"
              }}>
                <span style={{
                  fontSize: "16px",
                  color: "#666",
                  fontWeight: "500",
                  marginRight: "8px"
                }}>
                  1 Credit = ₹
                </span>
                <input
                  type="number"
                  step="0.01"
                  value={creditPrice}
                  onChange={(e) => setCreditPrice(e.target.value)}
                  placeholder="1"
                  style={{
                    border: "1px solid #DDD",
                    borderRadius: "8px",
                    padding: "12px",
                    fontSize: "18px",
                    fontWeight: "600",
                    color: "#000",
                    backgroundColor: "#F9FAFB",
                    maxWidth: "120px"
                  }}
                />
              </div>
              <button
                onClick={handleUpdateCreditPrice}
                disabled={saving}
                style={{
                  backgroundColor: "#6366F1",
                  borderRadius: "8px",
                  padding: "12px 24px",
                  border: "none",
                  color: "#fff",
                  fontSize: "15px",
                  fontWeight: "600",
                  cursor: saving ? "not-allowed" : "pointer",
                  opacity: saving ? 0.6 : 1
                }}
              >
                Save Credit Price
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Toast Notifications */}
      <Toaster
        show={toast.show}
        message={toast.message}
        type={toast.type}
        duration={5}
        onClose={hideToast}
      />
    </div>
  );
}